/**
 * storefront.de — the German buyer-facing storefront catalog, and the
 * content source of truth for this namespace: every value below is
 * byte-faithful to the audited Du copy in
 * `packages/views/src/storefront/{landing,event-header,programme-day-selector,
 * type-selector,event-page,checkout-disclosures}.view.ts` —
 * `storefront.en.ts` is the translation column.
 * Typed against `storefront.en.ts`'s shape via `CatalogShape` so a missing
 * or extra key here is a compile error, composed into the shared `de`
 * object by `messages.de.ts`.
 */
import type { CatalogShape } from "./catalog-shape.ts";
import type { storefront as storefrontEn } from "./storefront.en.ts";

export const storefront: CatalogShape<typeof storefrontEn> = {
	brandBar: {
		homeAriaLabel: "{{brand}}: zu allen Events",
		homeAriaLabelUnnamed: "Zur Startseite",
	},
	orders: {
		title: "Deine Bestellungen",
		entryLabel: "Deine Bestellungen",
		ticketsHere: "Deine Tickets sind hier",
		browserNote: "Alles, was in diesem Browser gekauft wurde.",
		loadingAriaLabel: "Deine Bestellungen werden geladen",
		ticketsOne: "1 Ticket",
		ticketsOther: "{{count}} Tickets",
		ticketLabelOne: "Ticket",
		ticketLabelOther: "Tickets",
		noteAuthorised:
			"Deine Zahlung l\u00e4uft noch. Deine Tickets erscheinen hier, sobald sie durch ist.",
		noteRefunded:
			"Diese Bestellung wurde erstattet \u2014 diese Tickets funktionieren am Einlass nicht mehr.",
		noteCancelled:
			"Diese Bestellung wurde storniert \u2014 diese Tickets funktionieren am Einlass nicht mehr.",
		emptyTitle: "Hier ist noch nichts",
		emptyBody:
			"Bestellungen bleiben in dem Browser, in dem sie gekauft wurden, und in diesem wurde noch nichts gekauft. Deine Tickets auf dem Handy oder in einem anderen Browser gekauft? Der Link in deiner Best\u00e4tigungsmail \u00f6ffnet sie \u00fcberall.",
		emptyCta: "Events entdecken",
		errorTitle: "Wir konnten deine Bestellungen nicht laden",
		errorBody:
			"Versuch es gleich noch einmal. Der Link in deiner Best\u00e4tigungsmail \u00f6ffnet deine Tickets in der Zwischenzeit weiterhin.",
		cappedNote: "Wir zeigen deine {{count}} neuesten Bestellungen.",
	},
	eventIdentity: {
		venueMapAriaLabel: "{{venue}}: Karte öffnen",
		entryInfoAriaLabel: "Zugangsinformationen",
		descriptionButtonAriaLabel: "Mehr Informationen zu {{name}}",
		nameButtonAriaLabel: "{{name}} — vollständig anzeigen",
	},
	eventDescription: {
		closeAriaLabel: "Schließen",
		venueMapAriaLabel: "{{venue}}: Karte öffnen",
		entryInfoAriaLabel: "Zugangsinformationen",
	},
	landing: {
		loadingAriaLabel: "Events werden geladen",
		emptyTitle: "Noch keine Events",
		emptyBody:
			"Die ersten Events von {{name}} erscheinen hier — schau bald wieder vorbei.",
		// vergangenes (neuter singular, no article) / vergangene (plural) —
		// both are the correct declension; neither is a typo of the other.
		pastOne: "1 vergangenes Event",
		pastOther: "{{count}} vergangene Events",
		soldOutBadge: "Ausverkauft",
		refundButton: "Tickets zurückgeben",
	},
	eventHeader: {
		notFoundTitle: "Dieses Event gibt es nicht mehr",
		notFoundBody:
			"Vielleicht ist es vorbei, oder der Link ist veraltet. Alle aktuellen Events findest du auf der Startseite.",
		backToEvents: "Zu allen Events",
		posterAriaLabel: "{{name}}: Plakat vergrößern",
	},
	programmeDaySelector: {
		groupAriaLabel: "Tag auswählen",
		// The whole pill read as one sentence: the calendar pill stacks
		// weekday, day and month as three separate lines, and read out one by
		// one they are three fragments rather than a date. The entry hours
		// belong in it because the chip is a choice about when the door is
		// open, not just about a date.
		dayAriaLabel: "{{day}}, {{opensAt}} – {{closesAt}}",
		// Same sentence, plus what the chip's dot says silently to everyone
		// else — this day is already in the order.
		dayInOrderAriaLabel:
			"{{day}}, {{opensAt}} – {{closesAt}} — bereits im Warenkorb",
	},
	typeSelector: {
		soldOutTitle: "Ausverkauft",
		soldOutBody:
			"Für dieses Event sind aktuell keine Tickets mehr verfügbar. Alle aktuellen Events findest du auf der Startseite.",
		// Nothing in this catalog is offered to the public — every type is for
		// the organiser's team or behind a code. Nothing sold out, so it never
		// says so.
		notOnSaleTitle: "Nicht im Online-Verkauf",
		notOnSaleBody:
			"Tickets für dieses Event gibt es nicht online. Alle aktuellen Events findest du auf der Startseite.",
		// The day, not the event: every other chip still leads somewhere, so
		// this says which one is gone and where to go next — never "for this
		// event", which reads as a dead event while five days are still on
		// sale.
		daySoldOutTitle: "Für diesen Tag ausverkauft",
		daySoldOutBody:
			"Für diesen Tag sind aktuell keine Tickets mehr verfügbar. Wähle oben einen anderen Tag.",
		// The band beneath the day's list — the tickets that are not tied to
		// one day. It names what they ARE, so nobody reads them as one more of
		// today's options and wonders whether they need one per day.
		wholeEventTitle: "Für das ganze Event",
		orderCap: "Maximal {{cap}} Tickets pro Bestellung.",
		orderCapOne: "Ein Ticket pro Bestellung.",
		free: "Kostenlos",
		volumeDiscount: "ab {{minQty}} Tickets {{price}}",
		lowStock: "Nur noch wenige",
		soldOutAfterSelection:
			"Inzwischen ausverkauft — deine Auswahl ist nicht garantiert",
		decrementAriaLabel: "{{name}}: Anzahl verringern",
		incrementAriaLabel: "{{name}}: Anzahl erhöhen",
		soldOutStatus: "Ausverkauft",
		opensOn: "ab {{date}}",
		saleEnded: "Verkauf beendet",
		basketLocked:
			"Gesperrt, während wir deine Zahlung bestätigen. — {{editBasket}}",
		editBasket: "Warenkorb bearbeiten",
		descriptionButtonAriaLabel: "Mehr Informationen zu {{name}}",
		nameButtonAriaLabel: "{{name}} — vollständig anzeigen",
		descriptionBackLabel: "Zurück",
	},
	eventPage: {
		cardDisclosureLabel: "Mit Karte bezahlen",
		nameLabel: "Name",
		billingCountryLabel: "Rechnungsland",
		billingPostcodeLabel: "Rechnungs-Postleitzahl",
		billingCountryRequired: "Wähle dein Rechnungsland",
		billingPostcodeRequired: "Gib deine Rechnungs-Postleitzahl ein",
		nameRequired: "Bitte gib deinen Namen ein.",
		emailLabel: "E-Mail",
		emailRequired: "Bitte gib deine E-Mail-Adresse ein.",
		emailInvalid: "Bitte gib eine gültige E-Mail-Adresse ein.",
		addressLabel: "Straße, PLZ und Ort",
		addressReason:
			"Über {{ceiling}} muss deine Anschrift auf der Rechnung stehen.",
		addressRequired: "Bitte gib deine Anschrift ein.",
		refundButton: "Tickets zurückgeben",
		jumpBar: {
			ticketOne: "{{qty}} Ticket · {{total}}",
			ticketOther: "{{qty}} Tickets · {{total}}",
			payCta: "Zur Zahlung",
		},
		payArea: {
			payLabel: "Bezahlen · {{total}}",
			// payLabel's verb without the total — the word the terms clause
			// quotes, so the quote reads as the button the buyer is looking at.
			payVerb: "Bezahlen",
			payLabelSettling: "Aktualisiert …",
			contactNeededToast: "Bitte Name und E-Mail-Adresse eingeben.",
			addressNeededToast: "Bitte Anschrift für die Rechnung eingeben.",
			freeOrder: "Kostenlos bestellen",
			unavailable: "Zahlung ist gerade nicht verfügbar.",
			salesPaused: "Der Ticketverkauf ist gerade pausiert.",
			noPaymentSession: "Zahlung ist noch nicht bereit",
			testModeBanner: "Testmodus · keine echte Zahlung",
			disclosures: {
				terms: "Mit »{{action}}« akzeptierst du unsere {{terms}}.",
				adsReport:
					"Nach dem Kauf melden wir den Bestellwert und deine E-Mail-Adresse in gehashter Form an Meta — du kannst dem jederzeit widersprechen.",
				totalSingleRate: "{{total}} inkl. {{rate}} % MwSt.",
				totalMixedRate: "{{total}} inkl. MwSt.",
			},
		},
		statusLine: {
			confirming: "Zahlung wird bestätigt…",
			prepareUnavailable: "Zahlungen sind vorübergehend nicht verfügbar.",
			retry: "Erneut versuchen",
			preparing: "Zahlung wird vorbereitet…",
			syncing: "Betrag wird aktualisiert…",
		},
		notice: {
			insufficientFunds: {
				headline: "Die Karte hat nicht genug Deckung",
				body: "Es wurde nichts abgebucht. Nutz eine andere Karte oder versuch es später noch einmal — deine Auswahl bleibt bestehen.",
			},
			expiredCard: {
				headline: "Die Karte ist abgelaufen",
				body: "Es wurde nichts abgebucht. Nutz eine Karte, die noch gültig ist — deine Auswahl bleibt bestehen.",
			},
			cardDetails: {
				headline: "Die Kartendaten stimmen nicht",
				body: "Es wurde nichts abgebucht. Prüf Kartennummer, Ablaufdatum und Sicherheitscode — deine Auswahl bleibt bestehen.",
			},
			// The only member the buyer fixes on this page rather than on a card:
			// name and e-mail are fields this form itself collects, so the copy
			// points back up at them.
			contactDetails: {
				headline: "Die Kontaktdaten stimmen nicht",
				body: "Es wurde nichts abgebucht. Prüf Name und E-Mail-Adresse weiter oben — deine Auswahl bleibt bestehen.",
			},
			authentication: {
				headline: "Die Bestätigung bei deiner Bank fehlt",
				body: "Es wurde nichts abgebucht. Versuch es noch einmal und schließ die Bestätigung ab — deine Auswahl bleibt bestehen.",
			},
			// Absorbs stolen, lost, fraud-suspected and risk declines. The buyer is
			// never told their card is reported stolen — that verdict is the bank's
			// to deliver, to its own customer, and repeating it here would accuse a
			// buyer the vendor's own account can have wrong.
			bankDeclined: {
				headline: "Deine Bank hat die Zahlung abgelehnt",
				body: "Es wurde nichts abgebucht. Frag bei deiner Bank nach oder nutz eine andere Karte — deine Auswahl bleibt bestehen.",
			},
			// The PIN is what is locked, and it is never entered on this page —
			// naming it would send the buyer looking for a field that does not
			// exist, so the copy names only the move that works here.
			cardLocked: {
				headline: "Die Karte ist gesperrt",
				body: "Es wurde nichts abgebucht. Nutz eine andere Karte, denn die Sperre kann nur deine Bank aufheben — deine Auswahl bleibt bestehen.",
			},
			// A limit lifts on its own, which is why "später" leads here and the
			// second card follows it — the reverse of insufficientFunds, where the
			// money may never arrive on that card at all.
			limitReached: {
				headline: "Das Limit der Karte ist erreicht",
				body: "Es wurde nichts abgebucht. Versuch es später noch einmal oder nutz eine andere Karte — deine Auswahl bleibt bestehen.",
			},
			cardNotActivated: {
				headline: "Die Karte ist noch nicht freigeschaltet",
				body: "Es wurde nichts abgebucht. Schalt sie bei deiner Bank frei oder nutz eine andere Karte — deine Auswahl bleibt bestehen.",
			},
			cardNotSupported: {
				headline: "Diese Karte wird hier nicht akzeptiert",
				body: "Es wurde nichts abgebucht. Nutz eine andere Karte — deine Auswahl bleibt bestehen.",
			},
			// The retry is offered, never promised: this class also absorbs
			// deterministic configuration failures on our side, where the identical
			// attempt fails identically. "später" is the whole hedge — the checkout
			// page carries no contact channel that is guaranteed to render, so the
			// copy points at coming back rather than at reaching us.
			technical: {
				headline: "Das lag an uns, nicht an deiner Karte",
				body: "Es wurde nichts abgebucht. Versuch es später noch einmal — deine Auswahl bleibt bestehen.",
			},
			// The unmapped fallback: the vendor named no cause, or one this
			// contract does not carry. Not "Deine Karte wurde abgelehnt" — the
			// sharper headline would sometimes be a lie.
			refused: {
				headline: "Die Zahlung ist nicht durchgegangen",
				body: "Es wurde nichts abgebucht. Versuch es noch einmal oder nutz eine andere Karte — deine Auswahl bleibt bestehen.",
			},
			// The one member that cannot end on "deine Auswahl bleibt bestehen":
			// the checkout the selection belonged to is exactly what is gone, so
			// the copy sends the buyer back to the start instead of promising a
			// basket that will not be there.
			orderStale: {
				headline: "Diese Bestellung ist nicht mehr gültig",
				body: "Es wurde nichts abgebucht. Lad die Seite neu und wähl deine Tickets noch einmal aus.",
			},
			blocked: {
				headline: "Zahlungen sind vorübergehend nicht verfügbar.",
				body: "",
				remedy: "Erneut versuchen",
			},
			retryPay: "Erneut versuchen · {{total}}",
		},
		refusal: {
			title: "Das hat leider nicht geklappt",
			body: "Die Tickets, die du ausgewählt hast, hat jemand anderes bekommen, bevor deine Zahlung durchgehen konnte. Es wurde kein Geld abgebucht — falls deine Bank den Betrag als reserviert anzeigt, verschwindet das von selbst, meist innerhalb weniger Tage.",
			bodyBasketChanged:
				"Dein Warenkorb hat sich geändert, bevor die Zahlung durchging — es wurde nichts abgebucht.",
			bodyGeneric:
				"Deine Zahlung wurde unterbrochen — es wurde nichts abgebucht.",
			restart: "Zurück zur Ticketauswahl",
			continueWithTotal: "Mit aktualisiertem Betrag fortfahren",
		},
		processing: {
			title: "Deine Bestellung wird verarbeitet …",
			body: "Einen Moment — bitte lass dieses Fenster geöffnet.",
		},
	},
	// The buyer's voucher field — the trigger on the § 312j price band
	// (voucher-code.view.ts) and the overlay it opens
	// (voucher-code-entry.view.ts).
	//
	// Every refusal cause renders ONE of these sentences and never names which
	// rule fired: unknown, expired, exhausted, out of scope, per-person cap and
	// the attempt cap are indistinguishable here by design, because a refusal
	// that distinguishes its causes is an oracle over which codes exist and how
	// many redemptions each has left. `dropped` is the one exception and is not
	// a refusal: it names a code this buyer already saw accepted, so saying it
	// stopped fitting their own selection discloses nothing they were not shown.
	voucher: {
		row: "Code einlösen",
		triggerAppliedAriaLabel: "Code {{code}} — Code ändern",
		label: "Rabatt- oder Zugangscode",
		placeholder: "z. B. PRESSE25",
		apply: "Einlösen",
		remove: "Entfernen",
		done: "Fertig",
		cancel: "Schließen",
		checking: "Dein Code wird geprüft …",
		// Never "Rabatt angewendet": a code that only reveals a hidden ticket
		// type, and one whose discount finds nothing eligible yet, both apply
		// with nothing off — the words would contradict a basket that did not
		// move.
		applied: "Code angewendet.",
		appliedUnlock: "Code angewendet. {{types}} steht dir jetzt zur Auswahl.",
		appliedUnlockOnly:
			"Code angewendet. {{types}} steht dir jetzt zur Auswahl. Am Preis deiner aktuellen Auswahl ändert sich dadurch nichts.",
		youSaved: "Du sparst {{amount}}",
		showTickets: "Zur Ticketauswahl",
		rules:
			"Codes gelten oft nur für ein bestimmtes Event, für bestimmte Tickets, für eine begrenzte Zeit oder nur einmal pro Person. Groß- und Kleinschreibung spielt keine Rolle.",
		refusedHeadline: "Dieser Code lässt sich nicht einlösen",
		refusedBody:
			"Codes gelten oft nur für ein bestimmtes Event, für bestimmte Tickets, für eine begrenzte Zeit oder nur einmal pro Person. Prüf die Schreibweise — auf Groß- und Kleinschreibung kommt es nicht an.",
		refusedAgain:
			"Prüf die Schreibweise noch einmal. Wenn es weiter nicht klappt, frag bei der Stelle nach, die dir den Code gegeben hat — wir können hier nicht sehen, woran es liegt.",
		spentHeadline: "Zu viele Versuche",
		spentBody:
			"Für diese Bestellung nehmen wir keinen weiteren Code mehr an. Schließ die Bestellung ohne Code ab, oder fang mit einer neuen an, wenn du den richtigen Code hast.",
		dropped: "{{code}} passt nicht mehr zu deiner Auswahl und wurde entfernt.",
	},
	orderAccess: {
		notFound: {
			title: "Dieser Link öffnet keine Tickets",
			body: "Entweder ist die Adresse unvollständig — dann prüf den Link in der Nachricht, mit der du ihn bekommen hast. Oder die Tickets wurden inzwischen zurückgeholt — dann gilt dieser Link nicht mehr. Wenn dir jemand den Link geschickt hat, frag dort nach.",
			cta: "Events entdecken",
		},
		success: {
			headline: "Du bist dabei.",
			subline: "Dein Ticket für {{eventName}} ist bereit.",
		},
		qrHint: "Wird beim Einlass gezeigt",
		qrAriaLabel: "Einlass-QR-Code für {{typeName}}",
		ticketAriaLabel: "Ticket {{displayId}}, {{typeName}}",
		thankYou: "Danke, dass du dabei bist.",
		handover: {
			// The card's one control is the question while nobody holds the
			// ticket and the answer once somebody does — so `heldBy` is both the
			// given control's own label and the line the accessible name ends on.
			whoFor: "Für wen?",
			heldBy: "Für {{name}} · {{channel}}",
			// The visible word alone would name every card's control identically
			// and a reader would hear "Für wen?" five times with nothing to tell
			// the tickets apart.
			whoForAriaLabel: "Für wen ist {{typeName}} {{displayId}}?",
			heldByAriaLabel: "{{typeName}} {{displayId}}: {{holder}}",
			channel: {
				whatsapp: "per WhatsApp",
				email: "per E-Mail",
				share: "geteilt",
				inPerson: "persönlich",
			},
			// The three scopes the rail's own switch offers, in the order they
			// stand: the whole book, then the reader's part, then the other. Each
			// is a chip's whole visible label and therefore its spoken name too,
			// so none of them says anything a sighted reader does not also read.
			// "Noch {{count}} bei dir" is the sheet's own wording for the same
			// fact, so one page never names it two ways.
			tally: {
				all: "Alle {{count}}",
				yours: "Noch {{count}} bei dir",
				given: "{{count}} abgegeben",
			},
			sheet: {
				retry: "Fehler · Erneut versuchen",
				// The buyer tapped "Für wen?" to get here, so the sheet carries
				// that same question as its heading rather than asking a second,
				// differently-worded one; the field below it is the answer slot.
				title: "Für wen?",
				nameLabel: "Name",
				namePlaceholder: "Vorname genügt",
				// The name is stored and readable by everyone who scans at the
				// door, so the hint gives the buyer's own reason for it — recognising
				// the person later — rather than a rule about what not to type.
				nameHint:
					"Ein Vorname oder Spitzname reicht, damit du später weißt, wer welches Ticket hat.",
				// The sheet's header names what is going before it asks anything
				// else: the tapped ticket by type and display id, the per-type
				// tally once the pick grows past it, and — if the buyer clears
				// the pick — that nothing is going.
				ticketsOne: "{{type}} · {{displayId}}",
				ticketsPerType: "{{count}}× {{type}}",
				ticketsNone: "Noch kein Ticket ausgewählt",
				// The picker's own summary: an invitation while the pick is the
				// tapped ticket alone, the live count the moment it is more, so a
				// folded-away second ticket is never invisible.
				pickerAdd: "Weitere Tickets hinzufügen",
				pickerCount: "{{count}} Tickets ausgewählt",
				wholeEvent: "Für das ganze Event",
				heldByYou: "Noch {{count}} bei dir",
				heldByOther: "{{count}} bei {{name}}",
				typeOnDay: "{{type}}, {{date}}",
				typeWholeEvent: "{{type}}, ganzes Event",
				rowAriaLabel: "{{type}}, noch {{count}} bei dir",
				addAriaLabel: "{{type}} hinzufügen",
				removeAriaLabel: "{{type}} entfernen",
				qtyAriaLabel: "Anzahl {{type}}",
				channelLabel: "Und wie?",
				whatsapp: "Per WhatsApp senden",
				// One tap, one meaning: it opens the overlay that asks where the
				// mail goes. The sending itself is the deployment's, so there is no
				// second beat here where the buyer would have to open a draft.
				email: "Per E-Mail senden",
				share: "Teilen …",
				// Covers both cases that send nothing: already handed over in
				// person, and "ich schicke den Link später" — a buyer without the
				// address to hand must still be able to keep the Vermerk.
				inPerson: "Nur vermerken, nicht senden",
				preparing: "Link wird vorbereitet …",
				// The only answer this page gets that the buyer has to do
				// something about, so it stands on the sheet rather than passing by
				// as a toast: it says what did NOT happen, that the handover itself
				// is untouched, and that the way out is the exits right beneath it.
				// "Konnten wir nicht senden", never "nicht zugestellt" — what came
				// back is the provider refusing the send, and no mailbox was ever
				// looked at.
				mailRefused: {
					headline: "Die E-Mail konnten wir nicht senden",
					body: "Für {{name}} vermerkt ist es trotzdem, und der Link gilt. Schick ihn ihr oder ihm auf einem anderen Weg.",
				},
				reclaim: "Tickets zurückholen",
			},
			recordedToast: "Für {{name}} vermerkt.",
			// The one exit that knows more than "the message left this page": the
			// mail rides the same call that records the give, so its reply says
			// the send was accepted. "Unterwegs", never "zugestellt" — nothing
			// reports arrival back, on this channel any more than the others.
			sentToast: "Für {{name}} vermerkt. Der Link ist per E-Mail unterwegs.",
			// States exactly what this page knows: the record is saved, and the
			// carrier was opened. No channel reports back, so none may claim the
			// message arrived.
			openedToast:
				"Für {{name}} vermerkt. Die Nachricht ist geöffnet — senden musst du sie noch selbst.",
			reclaimedToast: "Tickets sind wieder bei dir.",
			error: "Das hat nicht geklappt. Versuch es noch einmal.",
			sendError:
				"Der Vermerk steht, aber die Nachricht ließ sich nicht öffnen. Tippe am Ticket auf den Namen, um den Link noch einmal zu schicken.",
			// The E-Mail exit's own overlay: one address, one send, nothing else.
			emailAddress: {
				retryMail: "Vermerkt · Erneut senden",
				title: "Per E-Mail senden",
				lead: "An welche Adresse soll der Link für {{name}} gehen?",
				label: "E-Mail-Adresse",
				placeholder: "name@beispiel.de",
				// Says out loud what the deployment does with an address the buyer
				// typed for somebody else: it sends this one mail with it and keeps
				// nothing, which is also why sending the link again asks again.
				hint: "Wird nur für diese eine Mail benutzt und nicht gespeichert.",
				send: "Link senden",
				// Names what is wrong and asks for a look, rather than reciting a
				// rule the buyer never agreed to follow.
				invalid:
					"Diese E-Mail-Adresse stimmt so nicht. Schau sie dir noch mal an.",
			},
			message: {
				subject: "Dein Ticket für {{eventName}}",
				subjectPlural: "Deine Tickets für {{eventName}}",
				greeting: "Hi {{name}},",
				lead: "hier ist dein Ticket für {{eventName}}:",
				leadPlural: "hier sind deine {{count}} Tickets für {{eventName}}:",
				ticketLine: "{{typeName}} · {{displayId}}",
			},
		},
		artifacts: {
			pdf: "Ticket als PDF",
			ics: "Zum Kalender hinzufügen",
			receipt: "Rechnung",
			walletApple: "Apple Wallet",
			walletGoogle: "Google Wallet",
			preparing: "{{artifact}}: Wird vorbereitet",
		},
		downloadTickets: {
			label: "Tickets herunterladen",
			ariaLabel: "Alle Tickets herunterladen",
			// One card is rasterized at a time, so the wait has a real position in
			// it — named here rather than left as an anonymous hourglass, because
			// a large order spends real seconds in it.
			preparingAriaLabel: "Ticket {{done}} von {{total}} wird gespeichert",
			progress: "{{done}}/{{total}}",
			error:
				"Tickets konnten nicht vorbereitet werden. Versuch es später erneut.",
		},
		shareOrder: {
			label: "Bestellung teilen",
			ariaLabel: "Diese Bestellung teilen",
			text: "Tickets für {{eventName}}",
			savedToast: "Link kopiert",
			error: "Link konnte nicht geteilt werden. Versuch es später erneut.",
		},
		walletGoogleBadge: {
			ariaLabel: "Zu Google Wallet hinzufügen",
			ariaLabelIndexed: "Zu Google Wallet hinzufügen, {{index}} von {{count}}",
			preparingAriaLabel: "Zu Google Wallet hinzufügen: Wird vorbereitet",
		},
		walletAppleBadge: {
			ariaLabel: "Zu Apple Wallet hinzufügen",
			ariaLabelIndexed: "Zu Apple Wallet hinzufügen, {{index}} von {{count}}",
			preparingAriaLabel: "Zu Apple Wallet hinzufügen: Wird vorbereitet",
		},
		ticketActions: {
			ariaLabel: "{{typeName}}: sichern",
			savingAriaLabel: "{{typeName}} wird gespeichert",
			unavailableAriaLabel: "{{typeName}}: kann nicht mehr gesichert werden",
			saveImage: "Als Bild speichern",
			retryImage: "Bild erneut speichern",
		},
		ticketStatus: {
			valid: "Gültig",
			redeemed: "Bereits eingecheckt",
			cancelled: "Storniert",
		},
		voided: {
			refundedTitle: "Diese Bestellung wurde erstattet",
			refundedBody:
				"Der volle Betrag wurde an dein ursprüngliches Zahlungsmittel zurückerstattet. Diese Tickets berechtigen nicht mehr zum Einlass.",
			cancelledTitle: "Diese Bestellung wurde storniert",
			cancelledBody:
				"Diese Tickets berechtigen nicht mehr zum Einlass. Falls dir eine Erstattung zusteht, ist sie bereits an dein ursprüngliches Zahlungsmittel unterwegs.",
		},
	},
	refund: {
		title: "Tickets zurückgeben",
		openUntil: "Rückgabe bis {{date}}",
		defaultMessage:
			"Lade deine Tickets hoch. Einzelne Tickets aus einer Bestellung gehen auch — der Rest bleibt gültig.",
		pick: "Tickets auswählen",
		dropAddMore: "Weitere",
		localOnly: "Die Dateien bleiben auf deinem Gerät — wir lesen nur den Code.",
		pageLabel: "Seite {{page}}",
		removeAriaLabel: "{{fileName}} entfernen",
		thumbAlt: "Ticket aus {{fileName}}",
		fileDecoding: "Wird gelesen …",
		fileChecking: "Wird geprüft …",
		fileCheckingSlow: "Wird noch geprüft …",
		fileNoCode: "Kein Code im Bild",
		fileNotOurs: "Kein Ticket von uns",
		fileUnfinished: "Nicht fertig",
		fileReaderFailed: "Seite neu laden",
		reloadAriaLabel: "Seite neu laden",
		fileDuplicate: "Dieses Ticket ist doppelt",
		fileWrongType: "Nur Bilder und PDFs",
		fileOverCap: "Nicht mehr aufgenommen",
		fileStalled: "Konnte nicht geprüft werden",
		capReached: "Mehr als {{cap}} gehen nicht auf einmal.",
		confirmFailed: "Das hat nicht geklappt. Versuch es noch einmal.",
		verdict: {
			notEligible: "Nicht erstattbar",
			alreadyRefunded: "Schon erstattet",
			alreadyScanned: "Schon gescannt",
			notFound: "Kein Ticket dazu",
			noneBody: "Davon kommt nichts zurück.",
			totalLabel: "Erstattungsbetrag",
			totalCount: "{{count}} Ticket",
			totalCountPlural: "{{count}} Tickets",
		},
		discountIncluded:
			"Jeder Betrag ist das, was für dieses Ticket bezahlt wurde — ein eingelöster Rabatt ist bereits abgezogen.",
		consequence:
			"Die Tickets werden entwertet, das Geld geht zurück auf die Karte, mit der bezahlt wurde.",
		action: "{{total}} erstatten",
		retryAction: "Erneut {{total}} erstatten",
		running: "Erstattung läuft …",
		settledHeadline: "Unterwegs",
		settled:
			"{{total}} sind unterwegs zurück auf die Karte, mit der bezahlt wurde.",
		again: "Weitere Tickets zurückgeben",
		closedBody: "Die Rückgabefrist ist am {{date}} abgelaufen.",
		notYetBody: "Die Rückgabe öffnet am {{date}}.",
		unconfiguredBody:
			"Für dieses Event gibt es keine Rückgabe. Gib dein Ticket gern weiter — am Einlass funktioniert es genauso.",
		contact: "Passt etwas nicht? Schreib uns an {{email}}.",
		pickTitle: "Um welches Event geht es?",
		pickNone: "Gerade nimmt kein Event Tickets zurück.",
	},
};
